import { createTenementInfoUrl } from "./tenementsGeoServer.js";

const emptyValue = "—";

function isPresent(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

export function escapeHtml(value) {
  if (!isPresent(value)) {
    return emptyValue;
  }

  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function firstPresent(properties, keys) {
  const key = keys.find((candidate) => isPresent(properties[candidate]));
  return key ? properties[key] : null;
}

function normaliseGroup(group) {
  const value = isPresent(group) ? String(group).trim().toUpperCase() : "";

  if (value === "ADDED") {
    return "Added";
  }

  if (value === "DROPPED") {
    return "Dropped";
  }

  return isPresent(group) ? String(group).trim() : "Tenement";
}

function popupRow(label, value) {
  return `
    <div class="tenementPopupRow">
      <span class="tenementPopupLabel">${escapeHtml(label)}</span>
      <span class="tenementPopupValue">${escapeHtml(value)}</span>
    </div>
  `;
}

export function buildTenementPopupHtml(feature) {
  const properties = feature && feature.properties ? feature.properties : {};
  const tenementId = firstPresent(properties, ["FMT_TENID", "TENID", "TENEMENT_ID", "TENEMENTID", "id"]);
  const tenementName = firstPresent(properties, ["TENNAME", "TENEMENT_NAME", "NAME", "PROJECT", "TITLE"]);
  const holder = firstPresent(properties, ["ALL_HOLDER", "HOLDER1", "HOLDER", "HOLDERS"]);
  const type = firstPresent(properties, ["TYPE", "TENTYPE", "TENEMENT_TYPE"]);
  const surveyStatus = firstPresent(properties, ["SURVSTATUS", "SURVEY_STATUS"]);
  const tenementStatus = firstPresent(properties, ["TENSTATUS", "STATUS"]);
  const endDate = firstPresent(properties, ["ENDDATE", "END_DATE", "EXPIRY", "EXPIRY_DATE"]);
  const group = normaliseGroup(properties._INVENTUM_GROUP);
  const groupClass = group.toLowerCase() === "added" ? "tenementPopupChipAdded" : group.toLowerCase() === "dropped" ? "tenementPopupChipDropped" : "";
  const title = tenementId || tenementName || "Tenement";
  const subtitle = tenementId && tenementName ? tenementName : holder;

  return `
    <div class="tenementPopupInfo">
      <div class="tenementPopupHeader">
        <span class="tenementPopupEyebrow">Inventum tenement</span>
        <span class="tenementPopupChip ${groupClass}">${escapeHtml(group)}</span>
      </div>
      <div class="tenementPopupTitle">${escapeHtml(title)}</div>
      ${isPresent(subtitle) ? `<div class="tenementPopupSubtitle">${escapeHtml(subtitle)}</div>` : ""}
      <div class="tenementPopupGrid">
        ${popupRow("Holder", holder)}
        ${popupRow("Type", type)}
        ${popupRow("Survey", surveyStatus)}
        ${popupRow("Status", tenementStatus)}
        ${popupRow("End date", endDate)}
      </div>
    </div>
  `;
}

function hasTenementProperties(feature) {
  const properties = feature && feature.properties ? feature.properties : {};
  return Boolean(
    firstPresent(properties, [
      "FMT_TENID",
      "TENID",
      "TENEMENT_ID",
      "TENEMENTID",
      "HOLDER1",
      "ALL_HOLDER",
      "TENSTATUS",
      "SURVSTATUS",
      "_INVENTUM_GROUP",
    ])
  );
}

function hasCoordinatePair(coordinates) {
  if (!Array.isArray(coordinates)) {
    return false;
  }

  if (coordinates.length >= 2 && Number.isFinite(Number(coordinates[0])) && Number.isFinite(Number(coordinates[1]))) {
    return true;
  }

  return coordinates.some(hasCoordinatePair);
}

function hasUsableGeometry(feature) {
  return !!(
    feature &&
    feature.type === "Feature" &&
    feature.geometry &&
    feature.geometry.type &&
    hasCoordinatePair(feature.geometry.coordinates)
  );
}

export function normaliseTenementFeatureData(data) {
  const rawFeatures = Array.isArray(data)
    ? data
    : data && data.type === "FeatureCollection" && Array.isArray(data.features)
      ? data.features
      : data && Array.isArray(data.features)
        ? data.features
        : data && data.type === "Feature"
          ? [data]
          : [];

  const features = rawFeatures.filter(hasUsableGeometry);

  if (features.length === 0) {
    return null;
  }

  return {
    type: "FeatureCollection",
    features,
  };
}

/**
 * Function to handle feature rendering for GeoJSON layers
 */
export function onEachFeature(feature, layer, openPopup) {
  if (!hasTenementProperties(feature)) {
    return;
  }

  layer.bindPopup(buildTenementPopupHtml(feature), { className: "tenementLeafletPopup", maxWidth: 360 });

  if (openPopup && typeof layer.openPopup === "function") {
    if (layer._map) {
      layer.openPopup();
    } else if (typeof layer.once === "function") {
      layer.once("add", () => layer.openPopup());
    }
  }
}

/**
 * Creates a style for GeoJSON features
 * @param {Object} feature - The GeoJSON feature
 * @param {string} defaultColor - Default color to use
 * @returns {Object} Style object
 */
export function createFeatureStyle(feature, defaultColor = "#3388ff") {
  // Return custom style if feature has specific properties
  if (feature.properties && feature.properties.style) {
    return feature.properties.style;
  }

  // Default style
  return {
    color: defaultColor,
    weight: 2,
    opacity: 1,
    fillOpacity: 0.2,
    fillColor: defaultColor,
  };
}

export { createTenementInfoUrl };
